import React, { useState, useEffect } from 'react';
import { Form, Input, Select, InputNumber, DatePicker, Button, Card, message, Space } from 'antd';
import deferredExpenseService from '../../../../services/deferredExpenseService';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
}

const DeferredExpenseForm: React.FC<Props> = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [vendorBills, setVendorBills] = useState<any[]>([]);
  const [referenceType, setReferenceType] = useState<string>('MANUAL_ENTRY');

  useEffect(() => {
    fetchAccounts();
    fetchVendorBills();
  }, []);

  const fetchAccounts = async () => {
    try {
      // Fetch accounts from API
      const accountService = await import('../../../../services/accountService');
      const allAccounts = await accountService.default.getAccounts();
      console.log('Fetched accounts:', allAccounts.length, 'accounts');
      console.log('Sample account:', allAccounts[0]);
      console.log('Sample account ID:', allAccounts[0]?.id, 'Type:', typeof allAccounts[0]?.id);

      // Filter accounts by type
      const assetAccounts = allAccounts.filter((acc) => acc.accountType === 'ASSET');
      const expenseAccountsList = allAccounts.filter((acc) => acc.accountType === 'EXPENSE');
      console.log('Asset accounts:', assetAccounts.length);
      console.log('Expense accounts:', expenseAccountsList.length);

      setAccounts(allAccounts);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      // Fallback to empty array if fetch fails
      setAccounts([]);
    }
  };

  const fetchVendorBills = async () => {
    // TODO: Fetch unpaid/posted vendor bills from API
    // For now, using mock data
    setVendorBills([
      { id: '1', billNo: 'BILL-2025-001', totalAmount: 12000 }
    ]);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      console.log('Form values received:', values);
      console.log('deferredAccountId from form:', values.deferredAccountId, 'Type:', typeof values.deferredAccountId);
      console.log('expenseAccountId from form:', values.expenseAccountId, 'Type:', typeof values.expenseAccountId);

      const data = {
        name: values.name,
        referenceType: values.referenceType,
        referenceId: values.referenceId,
        deferredAccountId: values.deferredAccountId,
        expenseAccountId: values.expenseAccountId,
        totalAmount: values.totalAmount,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
        recognitionMethod: values.recognitionMethod || 'STRAIGHT_LINE',
        frequency: values.frequency || 'MONTHLY'
      };

      console.log('Submitting deferred expense data:', data);
      console.log('Account IDs in data:', {
        deferredAccountId: data.deferredAccountId,
        expenseAccountId: data.expenseAccountId
      });

      const response = await deferredExpenseService.create(data);

      console.log('API Response:', response);

      // Check if response has id (successful creation) or success property
      if (response?.id || response?.success || response?.data) {
        message.success('Deferred expense created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response?.message || 'Failed to create deferred expense');
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to create deferred expense');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorBillChange = (billId: string) => {
    const bill = vendorBills.find((b) => b.id === billId);
    if (bill) {
      form.setFieldsValue({
        name: `Deferred Expense - ${bill.billNo}`,
        totalAmount: bill.totalAmount
      });
    }
  };

  const deferredAccounts = accounts.filter((acc) => acc.accountType === 'ASSET');
  const expenseAccounts = accounts.filter((acc) => acc.accountType === 'EXPENSE');

  return (
    <Card title="Create Deferred Expense">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          referenceType: 'MANUAL_ENTRY',
          recognitionMethod: 'STRAIGHT_LINE',
          frequency: 'MONTHLY'
        }}
      >
        <Form.Item
          name="referenceType"
          label="Reference Type"
          rules={[{ required: true }]}
        >
          <Select onChange={setReferenceType}>
            <Option value="MANUAL_ENTRY">Manual Entry</Option>
            <Option value="VENDOR_BILL">Vendor Bill</Option>
          </Select>
        </Form.Item>

        {referenceType === 'VENDOR_BILL' && (
          <Form.Item
            name="referenceId"
            label="Vendor Bill"
            rules={[{ required: true, message: 'Please select a vendor bill' }]}
          >
            <Select
              placeholder="Select vendor bill"
              onChange={handleVendorBillChange}
              showSearch
              optionFilterProp="children"
            >
              {vendorBills.map((bill) => (
                <Option key={bill.id} value={bill.id}>
                  {bill.billNo} - ${bill.totalAmount.toLocaleString()}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="e.g., Annual Insurance Premium" />
        </Form.Item>

        <Form.Item
          name="deferredAccountId"
          label="Deferred Expense Account (Asset)"
          rules={[{ required: true, message: 'Please select a deferred account' }]}
        >
          <Select placeholder="Select deferred account" showSearch optionFilterProp="children">
            {deferredAccounts.map((acc) => (
              <Option key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="expenseAccountId"
          label="Expense Account"
          rules={[{ required: true, message: 'Please select an expense account' }]}
        >
          <Select placeholder="Select expense account" showSearch optionFilterProp="children">
            {expenseAccounts.map((acc) => (
              <Option key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="totalAmount"
          label="Total Amount"
          rules={[
            { required: true, message: 'Please enter the total amount' },
            { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Enter total amount"
            precision={2}
            min={0.01}
            prefix="$"
          />
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="Recognition Period"
          rules={[{ required: true, message: 'Please select the recognition period' }]}
        >
          <RangePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
          />
        </Form.Item>

        <Form.Item
          name="frequency"
          label="Recognition Frequency"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="MONTHLY">Monthly</Option>
            <Option value="QUARTERLY">Quarterly</Option>
            <Option value="ANNUAL">Annual</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="recognitionMethod"
          label="Recognition Method"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="STRAIGHT_LINE">Straight-Line (Equal Distribution)</Option>
            <Option value="CUSTOM">Custom (Manual per Period)</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Deferred Expense
            </Button>
            <Button onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default DeferredExpenseForm;
